<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReady" :filename-prefix="title" @commonFiltersUpdated="commonFiltersUpdated($event)"
      @clickDownloadPdf="downloadPdf($event)" @clickDownloadExcel="downloadExcel($event)"
      @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer v-for="currency in currencies" v-bind:key="currency" :data-table="dataTables[currency]"
      :dimension-fields="dimensionColumns" :measure-fields="measureColumns[currency]" :group-headers="groupHeaders"
      :data-ready="dataReady" :custom-table-class="customTableClass" :totals-data="totalsData[currency]"
      ref="tableRenderers" />
    <b-alert v-if="isTokenBalanceNegative" variant="secondary" class="alert-component" show>
      <strong>{{ alertSubject }}</strong>
      {{ alertMessage }}
    </b-alert>
  </b-container>
</template>

<script>
import commonActions from "@/components/pages/commonActions";
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import TableRenderer from "@/components/TableRenderer.vue";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import { columnTypeEnum, calculatedColumnsEnum, groupKeyEnum } from "@/model/columnsEnums";
import paths from "@/router/pathConstants";
import _ from "lodash";
import { ConsumptionReport } from "@/router/routeNames";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: ConsumptionReport,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions
  },
  data() {
    return {
      commonFilters: [],
      timeDimension: null,
      dataReady: false,
      dataTables: {},
      totalsData: {},
      measureColumns: {},
      currencies: ["EUR"],
      dimensionColumns: [],
      groupHeaders: [],
      /** trigger du composant alert **/
      isTokenBalanceNegative: false,
    };
  },
  computed: {
    title() {
      return this.$t("title.consumption_report");
    },
    alertSubject() {
      return this.$t("ConsumptionReport.alert_subject");
    },
    alertMessage() {
      return this.$t("ConsumptionReport.alert_message");
    },
    customTableClass() {
      return "table-width-medium";
    },
  },
  mounted() {
    this.initDisplayTable();
  },
  methods: {
    // Initialisation du tableau : quelles colonnes affichées et dans quel ordre
    initDisplayTable() {
      this.dimensionColumns = TableTools.Helper.constructColumnTable(
        [dimensionEnum.SITE_NAME],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
    },

    // Construction des colonnes de mesure pour une devise spécifique
    buildMeasureColumnsForCurrency(currency) {
      return TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.TOTAL_REVENUE_5,
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_5,
          calculatedColumnsEnum.TOTAL_REVENUE_10,
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_10,
          calculatedColumnsEnum.TOTAL_REVENUE_20,
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_20,
          calculatedColumnsEnum.TOTAL_DATA_HT,
          calculatedColumnsEnum.TOTAL_DATA_TTC,
          calculatedColumnsEnum.NOT_SPENT,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null,
        true,
        currency
      );
    },

    // Mise à jour des filtres
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      this.updateTable(this.commonFilters);
    },

    // Mise à jour du tableau selon les filtres
    async updateTable(filters) {
      try {
        const { signal } = this.resetAbortController();
        this.dataReady = false;
        this.isTokenBalanceNegative = false;

        // Dimensions communes pour toutes les requêtes
        const commonDimensions = [
          dimensionEnum.SITE,
          dimensionEnum.SITE_NAME,
          dimensionEnum.SITE_CURRENCY,
        ];

        /** Modèle de requête pour les PurchaseHistory */
        const birthdayFilter = TableTools.Helper.getFilterPurchaseHistoryNotBirthday();
        const purchaseHistoryQuery = {
          measures: [
            mesureEnum.PURCHASEHISTORY_AMOUNT,
            mesureEnum.PURCHASEHISTORY_AMOUNT_HT,
            mesureEnum.PURCHASEHISTORY_AMOUNT_5_PERCENT,
            mesureEnum.PURCHASEHISTORY_AMOUNT_10_PERCENT,
            mesureEnum.PURCHASEHISTORY_AMOUNT_20_PERCENT,
          ],
          timeDimensions: this.timeDimension
            ? [{ ...this.timeDimension, dimension: dimensionEnum.PURCHASEHISTORY_DATETIME }]
            : [],
          dimensions: commonDimensions,
          filters: [...filters, birthdayFilter],
        }

        /** Modèle de requête pour les SnackHistory */
        const snackHistoryQuery = {
          measures: [
            mesureEnum.SNACKHISTORY_TOKEN_PRICE,
            mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
            mesureEnum.SNACKHISTORY_AMOUNT_5_PERCENT,
            mesureEnum.SNACKHISTORY_AMOUNT_10_PERCENT,
            mesureEnum.SNACKHISTORY_AMOUNT_20_PERCENT,
          ],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: commonDimensions,
          filters: filters,
        }

        /** Modèle de requêtes pour les OmniplayEvent */
        let omniplayEventQueries = [];

        omniplayEventQueries.push({
          // Totaux TTC et HT
          groupKey: groupKeyEnum.HIDDENGROUP_OMNIPLAYEVENT_TOTAL,
          query: {
            measures: [
              mesureEnum.OMNIPLAYEVENT_TOTALTTC,
              mesureEnum.OMNIPLAYEVENT_TOTALHT,
            ],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: commonDimensions,
            filters: filters,
          }
        });
        omniplayEventQueries.push({
          // Détail de transactions
          groupKey: groupKeyEnum.HIDDENGROUP_OMNIPLAYEVENT_DETAIL,
          query: {
            measures: [],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: [...commonDimensions, dimensionEnum.OMNIPLAYEVENT_TRANSACTIONS],
            filters: filters,
          }
        });

        /** Modèle de requête pour le solde de tokens non consommés  */
        const unusedTokensQuery = [
          {
            // Total des tokens achetés
            query: {
              measures: [mesureEnum.PURCHASEHISTORY_AMOUNT],
              timeDimensions: this.timeDimension
                ? [{ ...this.timeDimension, dimension: dimensionEnum.PURCHASEHISTORY_DATETIME }]
                : [],
              dimensions: commonDimensions,
              filters: filters,
            }
          },
          {
            // Total des tokens consommés
            query: {
              measures: [mesureEnum.GAMEHISTORY_TOKEN_PRICE],
              timeDimensions: this.timeDimension
                ? [{ ...this.timeDimension, dimension: dimensionEnum.GAMEHISTORY_DATETIME }]
                : [],
              dimensions: commonDimensions,
              filters: filters,
            }
          }
        ];

        /** Requêtes, traitements et insertions des données */
        const [
          purchaseHistoryQueryResult,
          snackHistoryQueryResult,
          omniplayEventQueriesResult,
          unusedTokensQueryResult] = await Promise.all(
            [
              TableTools.Helper.doRequest(purchaseHistoryQuery, { signal }),
              TableTools.Helper.doRequest(snackHistoryQuery, { signal }),
              TableTools.Helper.doMultipleRequest(omniplayEventQueries, { signal }),
              TableTools.Helper.doMultipleRequest(unusedTokensQuery, { signal })
            ]
          );

        const allData = this.mergeAndCompleteResults(
          purchaseHistoryQueryResult,
          snackHistoryQueryResult,
          omniplayEventQueriesResult,
          unusedTokensQueryResult
        );

        // Grouper les données par devise
        const groupedByCurrency = _.groupBy(allData, dimensionEnum.SITE_CURRENCY);

        // Si aucune donnée, initialiser avec EUR par défaut pour afficher un tableau vide
        if (Object.keys(groupedByCurrency).length === 0) {
          this.currencies = ['EUR'];
          this.dataTables = { 'EUR': [] };
        } else {
          this.currencies = Object.keys(groupedByCurrency).sort();
          this.dataTables = groupedByCurrency;
        }

        this.totalsData = {};
        this.measureColumns = {};

        // Colonnes pour les totaux
        const totalColumns = [
          calculatedColumnsEnum.TOTAL_REVENUE_5,
          calculatedColumnsEnum.TOTAL_REVENUE_10,
          calculatedColumnsEnum.TOTAL_REVENUE_20,
          calculatedColumnsEnum.TOTAL_DATA_HT,
          calculatedColumnsEnum.TOTAL_DATA_TTC,
          calculatedColumnsEnum.NOT_SPENT,
        ];

        const percentColumns = [
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_5,
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_10,
          calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_20,
        ];

        // Calculer les totaux et construire les colonnes pour chaque devise
        this.currencies.forEach((currency) => {
          const currencyData = this.dataTables[currency];
          this.measureColumns[currency] = this.buildMeasureColumnsForCurrency(currency);
          this.totalsData[currency] = TableTools.Helper.calculateTotalsData(currencyData, totalColumns);
          this.completeTotalsData(currencyData, percentColumns, this.totalsData[currency]);
        });

        this.dataReady = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    mergeAndCompleteResults(purchaseHistoryData, snackHistoryData, omniplayEventData, unusedTokensData) {
      let mergedResults = [];

      /** 1 - Traitement des données principales */
      if (purchaseHistoryData.length > 0 || snackHistoryData.length > 0) {
        const mergedBySite = new Map();

        const getOrInit = (row) => {
          const siteId = row[dimensionEnum.SITE];
          let target = mergedBySite.get(siteId);
          if (!target) {
            target = {
              [dimensionEnum.SITE]: siteId,
              [dimensionEnum.SITE_NAME]: row[dimensionEnum.SITE_NAME],
              [dimensionEnum.SITE_CURRENCY]: row[dimensionEnum.SITE_CURRENCY],
              [calculatedColumnsEnum.TOTAL_REVENUE_5]: 0,
              [calculatedColumnsEnum.TOTAL_REVENUE_10]: 0,
              [calculatedColumnsEnum.TOTAL_REVENUE_20]: 0,
              [calculatedColumnsEnum.TOTAL_DATA_TTC]: 0,
              [calculatedColumnsEnum.TOTAL_DATA_HT]: 0,
            };
            mergedBySite.set(siteId, target);
          }
          return target;
        };

        // PurchaseHistory
        for (const row of purchaseHistoryData) {
          const target = getOrInit(row);
          target[calculatedColumnsEnum.TOTAL_REVENUE_5] += row[mesureEnum.PURCHASEHISTORY_AMOUNT_5_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_REVENUE_10] += row[mesureEnum.PURCHASEHISTORY_AMOUNT_10_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_REVENUE_20] += row[mesureEnum.PURCHASEHISTORY_AMOUNT_20_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_DATA_TTC] += row[mesureEnum.PURCHASEHISTORY_AMOUNT] || 0;
          target[calculatedColumnsEnum.TOTAL_DATA_HT] += row[mesureEnum.PURCHASEHISTORY_AMOUNT_HT] || 0;
        }

        // SnackHistory
        for (const row of snackHistoryData) {
          const target = getOrInit(row);
          target[calculatedColumnsEnum.TOTAL_REVENUE_5] += row[mesureEnum.SNACKHISTORY_AMOUNT_5_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_REVENUE_10] += row[mesureEnum.SNACKHISTORY_AMOUNT_10_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_REVENUE_20] += row[mesureEnum.SNACKHISTORY_AMOUNT_20_PERCENT] || 0;
          target[calculatedColumnsEnum.TOTAL_DATA_TTC] += row[mesureEnum.SNACKHISTORY_TOKEN_PRICE] || 0;
          target[calculatedColumnsEnum.TOTAL_DATA_HT] += row[mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT] || 0;
        }

        // OmniplayEvent
        omniplayEventData.forEach(query => {
          const results = query.resultSet.loadResponse.results[0].data;
          if (!results || !results.length) return;

          const isTotalQuery = query.groupKey === groupKeyEnum.HIDDENGROUP_OMNIPLAYEVENT_TOTAL;
          const isDetailQuery = query.groupKey === groupKeyEnum.HIDDENGROUP_OMNIPLAYEVENT_DETAIL;

          if (isTotalQuery) {
            for (const row of results) {
              const target = getOrInit(row);
              target[calculatedColumnsEnum.TOTAL_DATA_TTC] += row[mesureEnum.OMNIPLAYEVENT_TOTALTTC] || 0;
              target[calculatedColumnsEnum.TOTAL_DATA_HT] += row[mesureEnum.OMNIPLAYEVENT_TOTALHT] || 0;
            }
          }

          if (isDetailQuery) {
            for (const row of results) {
              const target = getOrInit(row);
              const transactions = this.transactionsDataExtraction(row);

              transactions.forEach(transac => {
                switch (String(transac.VatRate)) {
                  case '5.5':
                    target[calculatedColumnsEnum.TOTAL_REVENUE_5] += transac.PriceTTC || 0;
                    break;
                  case '10':
                    target[calculatedColumnsEnum.TOTAL_REVENUE_10] += transac.PriceTTC || 0;
                    break;
                  case '20':
                    target[calculatedColumnsEnum.TOTAL_REVENUE_20] += transac.PriceTTC || 0;
                    break;
                  default:
                    break;
                }
              });
            }
          }
        });

        mergedResults = Array.from(mergedBySite.values());
      }

      /** 2 - Traitement des pourcentages */
      const percentKeys = [
        { revenue: calculatedColumnsEnum.TOTAL_REVENUE_5, percent: calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_5 },
        { revenue: calculatedColumnsEnum.TOTAL_REVENUE_10, percent: calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_10 },
        { revenue: calculatedColumnsEnum.TOTAL_REVENUE_20, percent: calculatedColumnsEnum.PERCENT_TOTAL_REVENUE_20 },
      ];

      mergedResults.forEach((site) => {
        const totalTTC = site[calculatedColumnsEnum.TOTAL_DATA_TTC] || 0;
        percentKeys.forEach(({ revenue, percent }) => {
          site[percent] = totalTTC ? ((site[revenue] || 0) / totalTTC) * 100 : 0;
        });
      });

      /** 3 - Traitement du solde de tokens non consommés */
      if (unusedTokensData.length === 2) {
        const purchasedBySite = Object.fromEntries(
          unusedTokensData[0].resultSet.loadResponse.results[0].data.map(
            row => [row[dimensionEnum.SITE], row[mesureEnum.PURCHASEHISTORY_AMOUNT] || 0]
          )
        );

        const usedBySite = Object.fromEntries(
          unusedTokensData[1].resultSet.loadResponse.results[0].data.map(
            row => [row[dimensionEnum.SITE], row[mesureEnum.GAMEHISTORY_TOKEN_PRICE] || 0]
          )
        );

        mergedResults.forEach(site => {
          const siteId = site[dimensionEnum.SITE];
          const unusedTokensBalance = (purchasedBySite[siteId] ?? 0) - (usedBySite[siteId] ?? 0);
          site[calculatedColumnsEnum.NOT_SPENT] = unusedTokensBalance;

          if (unusedTokensBalance < 0) {
            this.isTokenBalanceNegative = true;
          }
        });
      }

      return mergedResults;
    },
    transactionsDataExtraction(object) {
      try {
        const transactionsJson = object[dimensionEnum.OMNIPLAYEVENT_TRANSACTIONS];
        const parsedTransactions = JSON.parse(transactionsJson);
        return parsedTransactions?.Values || [];
      } catch (error) {
        console.warn('Error parsing transaction data:', error);
        return [];
      }
    },
    completeTotalsData(dataTable, keys, totalsDataObj) {
      keys.forEach((k) => {
        totalsDataObj[k] = _.meanBy(dataTable, (data) => data[k] || 0);
      });
    },

    // ----- EXPORTS -----
    downloadPdf(data) {
      if (
        this.dataReady
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (
        this.dataReady
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (
        this.dataReady
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const allData = [];

      // Assembler les données pour chaque devise
      this.currencies.forEach((currency, index) => {
        const renderer = this.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || this.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          this.dataTables[currency],
          this.dimensionColumns,
          this.measureColumns[currency],
          this.groupHeaders,
          sortKey,
          null,
          null,
          this.totalsData[currency]
        );

        // Ajouter un titre de section pour la devise
        allData.push({
          ...data,
          sectionTitle: currency
        });
      });

      TableTools.Helper.storeExportData(
        allData,
        this.title,
        subtitles,
        filename
      );
    },
  },
};
</script>

<style scoped>
.alert-component {
  max-width: 1200px;
  margin: 1rem auto;
  text-align: start;
  font-size: 15px;
}
</style>
